import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { button } from '@vitesse/base-ui/components/base-buttons';
import type { BackendClient } from '../../bridge/backend-client';
import type { SymbolCombo } from '../../../core/bridge/connect-client';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';
import './history-symbol-picker';
import { MAX_HISTORY_SYMBOLS } from './controller-history-section';

/*
 * <vitesse-history-combo-dialog> — manage SAVED SYMBOL COMBINATIONS: named sets
 * of 1..MAX_HISTORY_SYMBOLS symbol paths for the History tab, persisted org-wide
 * through the Backend Worker (user_data KV — the server-side sibling of the
 * tab's per-browser localStorage selection). One dialog, two views:
 *
 *   list — every saved combo (name + symbol chips) with Apply / Edit / Delete.
 *          Apply emits `combo-apply` (detail: the paths); the OWNER swaps the
 *          lanes — this component does no lane work and no IO of its own beyond
 *          the combo CRUD.
 *   edit — name input + the draft's symbol chips + "Add symbol", which opens
 *          the SAME <vitesse-history-symbol-picker> the tab uses, stacked on
 *          top (native <dialog> top layer, so z-order is free). Save is a
 *          single Put — the combo id is client-generated, so create and edit
 *          are the same verb.
 *
 * The list refetches on every open (fresh-per-open, like the picker): combos
 * are org-shared, so a teammate's edit must show up without a reload. Nested
 * dialogs' `dialog-close` bubbles — those handlers stopPropagation so closing
 * the picker/confirm can never close this dialog through the owner's handler.
 */

type ComboDraft = { id: string; name: string; symbols: string[]; isNew: boolean };

@customElement('vitesse-history-combo-dialog')
export class HistoryComboDialog extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() controllerId = '';
  @property({ type: Boolean }) open = false;
  /** The tab's current lanes — used to identify the saved set currently shown. */
  @property({ attribute: false }) currentSymbols: string[] = [];

  @state() private combos: SymbolCombo[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private view: 'list' | 'edit' = 'list';
  @state() private draft: ComboDraft | null = null;
  @state() private pickerOpen = false;
  @state() private deleteTarget: SymbolCombo | null = null;
  @state() private saving = false;
  @state() private deleting = false;

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM, matching the tab's other dialogs
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('open') && this.open) {
      // Fresh list per open — combos are org-shared, so yesterday's fetch may
      // already be a teammate's stale truth.
      this.view = 'list';
      this.draft = null;
      this.pickerOpen = false;
      this.deleteTarget = null;
      this.error = '';
      void this.loadCombos();
    }
  }

  private async loadCombos(): Promise<void> {
    if (!this.backend || !this.gatewayId || !this.controllerId) return;
    this.loading = true;
    try {
      this.combos = await this.backend.api.listSymbolCombos(this.gatewayId, this.controllerId);
      this.error = '';
    } catch (err) {
      this.error = t('controller.historyComboLoadFailed', { reason: (err as Error)?.message ?? String(err) });
    } finally {
      this.loading = false;
    }
  }

  // ── edit flow ──────────────────────────────────────────────────────────────

  private startNew(): void {
    this.draft = {
      id: crypto.randomUUID(),
      name: '',
      symbols: [],
      isNew: true,
    };
    this.error = '';
    this.view = 'edit';
  }

  private startEdit(combo: SymbolCombo): void {
    this.draft = { id: combo.id, name: combo.name, symbols: [...combo.symbolPaths], isNew: false };
    this.error = '';
    this.view = 'edit';
  }

  private backToList(): void {
    this.view = 'list';
    this.draft = null;
    this.error = '';
  }

  private updateDraftName(name: string): void {
    if (!this.draft) return;
    this.draft = { ...this.draft, name };
  }

  private addDraftSymbol(path: string): void {
    if (!this.draft || !path || this.draft.symbols.includes(path)) return;
    if (this.draft.symbols.length >= MAX_HISTORY_SYMBOLS) return;
    this.draft = { ...this.draft, symbols: [...this.draft.symbols, path] };
  }

  private removeDraftSymbol(path: string): void {
    if (!this.draft) return;
    this.draft = { ...this.draft, symbols: this.draft.symbols.filter((s) => s !== path) };
  }

  private async saveDraft(): Promise<void> {
    if (!this.backend || !this.draft || this.saving) return;
    const name = this.draft.name.trim();
    // A refused save must SAY so — same rule as the export dialog.
    if (!name) {
      this.error = t('controller.historyComboNameRequired');
      return;
    }
    if (this.draft.symbols.length === 0) {
      this.error = t('controller.historyComboNeedSymbol');
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      await this.backend.api.putSymbolCombo(this.gatewayId, this.controllerId, {
        id: this.draft.id,
        name,
        symbolPaths: this.draft.symbols,
      });
      await this.loadCombos();
      this.backToList();
    } catch (err) {
      this.error = t('controller.historyComboSaveFailed', { reason: (err as Error)?.message ?? String(err) });
    } finally {
      this.saving = false;
    }
  }

  // ── delete flow ────────────────────────────────────────────────────────────

  private async confirmDelete(): Promise<void> {
    const target = this.deleteTarget;
    if (!this.backend || !target || this.deleting) return;
    this.deleting = true;
    try {
      await this.backend.api.deleteSymbolCombo(this.gatewayId, this.controllerId, target.id);
      this.deleteTarget = null;
      await this.loadCombos();
    } catch (err) {
      this.error = t('controller.historyComboSaveFailed', { reason: (err as Error)?.message ?? String(err) });
      this.deleteTarget = null;
    } finally {
      this.deleting = false;
    }
  }

  // ── apply ──────────────────────────────────────────────────────────────────

  private apply(combo: SymbolCombo): void {
    this.dispatchEvent(new CustomEvent<string[]>('combo-apply', { detail: [...combo.symbolPaths] }));
  }

  private isCurrent(combo: SymbolCombo): boolean {
    if (combo.symbolPaths.length !== this.currentSymbols.length) return false;
    const displayed = new Set(this.currentSymbols);
    return (
      displayed.size === combo.symbolPaths.length && combo.symbolPaths.every((path) => displayed.has(path))
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────

  private renderList() {
    if (this.loading && this.combos.length === 0)
      return html`<div class="hcd-hint">${t('common.loading')}</div>`;
    if (this.combos.length === 0) return html`<div class="hcd-hint">${t('controller.historyComboEmpty')}</div>`;
    return html`<div class="hcd-list">
      ${this.combos.map(
        (c) => {
          const current = this.isCurrent(c);
          return html`<div class=${`hcd-row${current ? ' current' : ''}`}>
          <button
            class="hcd-apply"
            title=${t('controller.historyComboApply')}
            aria-pressed=${current ? 'true' : 'false'}
            @click=${() => this.apply(c)}
          >
            <span class="hcd-name-text">${c.name}</span>
            <span class="hcd-paths">${c.symbolPaths.join(' · ')}</span>
          </button>
          ${button({
            label: '',
            icon: 'pencil',
            color: 'neutral',
            size: 'sm',
            title: t('common.edit'),
            onClick: () => this.startEdit(c),
          })}
          ${button({
            label: '',
            icon: 'trash-2',
            color: 'neutral',
            size: 'sm',
            title: t('controller.historyComboDelete'),
            onClick: () => (this.deleteTarget = c),
          })}
        </div>`;
        },
      )}
    </div>`;
  }

  private renderEdit(draft: ComboDraft) {
    const full = draft.symbols.length >= MAX_HISTORY_SYMBOLS;
    const hasName = draft.name.trim().length > 0;
    const hasSymbol = draft.symbols.length > 0;
    return html`<div class="hcd-form">
      <label class="hcd-field">
        <span>${t('controller.historyComboName')}</span>
        <input
          class="hcd-name"
          type="text"
          maxlength="200"
          .value=${draft.name}
          @input=${(e: InputEvent) => this.updateDraftName((e.currentTarget as HTMLInputElement).value)}
        />
      </label>
      <div class="hcd-symbols">
        ${draft.symbols.map(
          (path) => html`<span class="hcd-chip">
            <span class="hcd-chip-path">${path}</span>
            <button class="hcd-chip-x" title=${t('controller.historyComboRemoveSymbol')} @click=${() => this.removeDraftSymbol(path)}>
              <vitesse-icon name="x" .size=${12}></vitesse-icon>
            </button>
          </span>`,
        )}
        ${button({
          label: t('controller.historyAddSymbol'),
          icon: 'plus',
          color: 'neutral',
          size: 'sm',
          disabled: full,
          title: full
            ? t('controller.historyMaxSymbols', { max: MAX_HISTORY_SYMBOLS })
            : t('controller.historyAddSymbol'),
          onClick: () => (this.pickerOpen = true),
        })}
      </div>
      <div class="hcd-requirements" aria-live="polite">
        <span class=${`hcd-requirement${hasName ? ' met' : ''}`}>
          ${hasName ? '✓' : '○'} ${t('controller.historyComboRequirementName')}
        </span>
        <span class=${`hcd-requirement${hasSymbol ? ' met' : ''}`}>
          ${hasSymbol ? '✓' : '○'} ${t('controller.historyComboRequirementSymbol')}
        </span>
      </div>
    </div>`;
  }

  render() {
    const canSave = !!this.draft?.name.trim() && !!this.draft.symbols.length;
    const saveTitle = !this.draft?.name.trim()
      ? t('controller.historyComboRequirementName')
      : !this.draft.symbols.length
        ? t('controller.historyComboRequirementSymbol')
        : t('controller.historyComboSave');
    return html`${this.styles()}
      <vitesse-dialog
        width="560px"
        .open=${this.open}
        heading=${this.view === 'edit'
          ? t(this.draft?.isNew ? 'controller.historyComboNewTitle' : 'controller.historyComboEditTitle')
          : t('controller.historyCombosTitle')}
      >
        ${!this.open
          ? nothing
          : html`${this.view === 'edit' && this.draft ? this.renderEdit(this.draft) : this.renderList()}
            ${this.error ? html`<div class="controller-history-status error hcd-error">${this.error}</div>` : nothing}`}
        <div slot="footer">
          ${this.view === 'edit'
            ? html`${button({ label: t('common.cancel'), color: 'neutral', onClick: () => this.backToList() })}
              ${button({
                label: t('controller.historyComboSave'),
                color: 'blue',
                disabled: this.saving || !canSave,
                title: saveTitle,
                onClick: () => void this.saveDraft(),
              })}`
            : button({
                label: t('controller.historyComboNew'),
                icon: 'plus',
                color: 'blue',
                onClick: () => this.startNew(),
              })}
        </div>
      </vitesse-dialog>
      <vitesse-history-symbol-picker
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .controllerId=${this.controllerId}
        .open=${this.pickerOpen}
        .taken=${this.draft?.symbols ?? []}
        @symbol-pick=${(e: CustomEvent<string>) => {
          this.addDraftSymbol(e.detail);
          this.pickerOpen = false;
        }}
        @dialog-close=${(e: Event) => {
          // The nested picker's close must not bubble on to the OWNER's
          // dialog-close handler and take this whole dialog down with it.
          e.stopPropagation();
          this.pickerOpen = false;
        }}
      ></vitesse-history-symbol-picker>
      <vitesse-confirm-dialog
        .open=${!!this.deleteTarget}
        heading=${t('controller.historyComboDeleteHeading')}
        message=${t('controller.historyComboDeleteMessage', { name: this.deleteTarget?.name ?? '' })}
        confirm-label=${t('controller.historyComboDelete')}
        danger
        ?busy=${this.deleting}
        @confirm=${() => void this.confirmDelete()}
        @dialog-close=${(e: Event) => {
          e.stopPropagation(); // same bubbling trap as the picker
          this.deleteTarget = null;
        }}
      ></vitesse-confirm-dialog>`;
  }

  /** Light-DOM styles scoped by the element tag, like the symbol picker's. */
  private styles() {
    return html`<style>
      vitesse-history-combo-dialog .hcd-hint {
        font-size: 12px;
        color: var(--text-secondary);
        opacity: 0.7;
        padding: 10px 2px;
      }
      vitesse-history-combo-dialog .hcd-list {
        display: flex;
        flex-direction: column;
        max-height: 48vh;
        overflow-y: auto;
      }
      vitesse-history-combo-dialog .hcd-row {
        display: flex;
        align-items: center;
        gap: 6px;
        border-bottom: 1px solid var(--sg-border-subtle);
        padding: 4px 8px 4px 0;
        transition: background 0.1s ease, box-shadow 0.1s ease;
      }
      vitesse-history-combo-dialog .hcd-row.current {
        background: rgb(from var(--blue) r g b / 0.16);
        border-radius: 6px;
        box-shadow: inset 3px 0 0 var(--blue), inset 0 0 0 1px rgb(from var(--blue) r g b / 0.45);
      }
      vitesse-history-combo-dialog .hcd-row.current .hcd-name-text {
        color: var(--blue);
      }
      vitesse-history-combo-dialog .hcd-apply {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding: 6px 8px;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: var(--text-primary);
        font: inherit;
        text-align: left;
        cursor: pointer;
        transition: background 0.1s ease;
      }
      vitesse-history-combo-dialog .hcd-apply:hover {
        background: rgb(from var(--blue) r g b / 0.12);
      }
      vitesse-history-combo-dialog .hcd-name-text {
        font-size: 13px;
        font-weight: 600;
      }
      vitesse-history-combo-dialog .hcd-paths {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 11px;
        color: var(--text-secondary);
        font-family: ui-monospace, 'SF Mono', monospace;
      }
      vitesse-history-combo-dialog .hcd-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      vitesse-history-combo-dialog .hcd-field {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12px;
        color: var(--text-secondary);
      }
      vitesse-history-combo-dialog .hcd-name {
        padding: 6px 8px;
        /* --sg-border-subtle is a hairline (row dividers), not enough contrast
           to read as an editable field's boundary — derive from the text
           color instead, so the outline is visible against either theme. */
        border: 1px solid rgb(from var(--text-primary) r g b / 0.3);
        border-radius: 6px;
        background: var(--sg-bg);
        color: var(--text-primary);
        font: inherit;
        font-size: 13px;
      }
      vitesse-history-combo-dialog .hcd-name:focus {
        outline: none;
        border-color: var(--blue);
      }
      vitesse-history-combo-dialog .hcd-symbols {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
      }
      vitesse-history-combo-dialog .hcd-requirements {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 14px;
        font-size: 11px;
        color: var(--text-secondary);
      }
      vitesse-history-combo-dialog .hcd-requirement.met {
        color: var(--green);
      }
      vitesse-history-combo-dialog .hcd-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        max-width: 100%;
        padding: 3px 4px 3px 8px;
        border: 1px solid var(--sg-border-subtle);
        border-radius: 999px;
        font-size: 11px;
        font-family: ui-monospace, 'SF Mono', monospace;
      }
      vitesse-history-combo-dialog .hcd-chip-path {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      vitesse-history-combo-dialog .hcd-chip-x {
        display: inline-flex;
        align-items: center;
        border: none;
        background: transparent;
        color: var(--text-secondary);
        cursor: pointer;
        padding: 2px;
      }
      vitesse-history-combo-dialog .hcd-chip-x:hover {
        color: var(--text-primary);
      }
      vitesse-history-combo-dialog .hcd-error {
        margin-top: 10px;
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-history-combo-dialog': HistoryComboDialog;
  }
}
